import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function headers() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function api() {
  return axios.create({ baseURL: API, headers: headers() });
}

export async function fetchProducts(params = {}) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.category) q.set('category', params.category);
  if (params.show_inactive) q.set('show_inactive', 'true');
  q.set('page', params.page || 1);
  q.set('limit', params.limit || 100);
  const { data } = await api().get(`/products?${q.toString()}`);
  return data;
}

export async function fetchProduct(id) {
  const { data } = await api().get(`/products/${id}`);
  return data;
}

export async function updateProduct(id, updates) {
  const { data } = await api().put(`/products/${id}`, updates);
  return data;
}

export async function toggleProductActive(id) {
  const { data } = await api().post(`/products/${id}/toggle-active`);
  return data;
}

export async function uploadProductImage(id, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api().post(`/products/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export function getFileUrl(path) {
  if (!path) return '';
  // Legacy images stored a fully-tokenized URL in the DB (e.g. ".../files/<path>?auth=<jwt>"),
  // which broke when the JWT expired. New uploads store the bare storage path, and we sign
  // them fresh here on every render using the current session token.
  if (/^https?:\/\//i.test(path)) {
    const m = path.match(/\/api\/files\/([^?]+)/);
    if (m) path = m[1];
    else return path;
  }
  const token = localStorage.getItem('token');
  return `${API}/files/${path}?auth=${token}`;
}

export async function fetchCategories() {
  const { data } = await api().get('/products/categories');
  return data.categories;
}

export async function fetchStats() {
  const { data } = await api().get('/products/stats');
  return data;
}

export async function syncProducts() {
  const { data } = await api().post('/products/sync');
  return data;
}

export async function configureProduct(productId) {
  const { data } = await api().post(`/products/${productId}/configure`);
  return data;
}

export async function fetchPrice(productId, options) {
  const { data } = await api().post(`/products/${productId}/price`, { options });
  return data;
}

export async function fetchSyncLogs() {
  const { data } = await api().get('/sync-logs');
  return data.logs;
}

export async function createUser(email, password, name, tierAccess) {
  const { data } = await api().post('/auth/create-user', { email, password, name, tier_access: tierAccess });
  return data;
}

export async function listUsers() {
  const { data } = await api().get('/auth/users');
  return data.users;
}

export async function deleteUser(email) {
  const { data } = await api().delete(`/auth/users/${email}`);
  return data;
}

export async function updateUserTiers(email, tierAccess) {
  const { data } = await api().put(`/auth/users/${email}/tiers`, { tier_access: tierAccess });
  return data;
}

export async function getPricingConfig(productId) {
  const { data } = await api().get(`/products/${productId}/pricing-config`);
  return data;
}

export async function updatePricingConfig(productId, config) {
  const { data } = await api().put(`/products/${productId}/pricing-config`, config);
  return data;
}

export async function updateManualPrice(productId, priceData) {
  const { data } = await api().put(`/products/${productId}/manual-price`, priceData);
  return data;
}

export async function getAllPricingConfigs() {
  const { data } = await api().get('/pricing-configs');
  return data.configs;
}

// Competitors
export async function createCompetitor(comp) {
  const { data } = await api().post('/competitors', comp);
  return data;
}
export async function listCompetitors() {
  const { data } = await api().get('/competitors');
  return data.competitors;
}
export async function updateCompetitor(id, comp) {
  const { data } = await api().put(`/competitors/${id}`, comp);
  return data;
}
export async function deleteCompetitor(id) {
  const { data } = await api().delete(`/competitors/${id}`);
  return data;
}
export async function setCompetitorPrice(entry) {
  const { data } = await api().post('/competitor-prices', entry);
  return data;
}
export async function getCompetitorPrices(productId, competitorId) {
  const q = competitorId ? `?competitor_id=${competitorId}` : '';
  const { data } = await api().get(`/competitor-prices/${productId}${q}`);
  return data.entries;
}
export async function deleteCompetitorPrice(entryId) {
  const { data } = await api().delete(`/competitor-prices/${entryId}`);
  return data;
}

// Admin: Price cache sync (manual re-fetch from vendor)
export async function startPriceSync() {
  const { data } = await api().post('/admin/sync-prices');
  return data;
}
export async function getPriceSyncStatus() {
  const { data } = await api().get('/admin/sync-prices/status');
  return data;
}
export async function getCachedPriceStats() {
  const { data } = await api().get('/admin/cached-prices/stats');
  return data;
}
export async function syncOneConfig(productId, options) {
  const { data } = await api().post(`/products/${productId}/sync-config`, { options });
  return data;
}
export async function startDeepSync() {
  const { data } = await api().post('/admin/deep-sync');
  return data;
}
export async function getDeepSyncStatus() {
  const { data } = await api().get('/admin/deep-sync/status');
  return data;
}
